import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Zap, X, ShieldCheck, Loader, RefreshCw } from "lucide-react";

const FALLBACK = {
  monthly: { price: 14.9 },
  annual: { price: 99.9 },
};

export default function UpClubBanner() {
  const [plans, setPlans] = useState(FALLBACK);
  const [checkout, setCheckout] = useState(null); // { plan_type }

  useEffect(() => {
    api.get("/public/upclub/plans")
      .then((r) => setPlans(r.data || FALLBACK))
      .catch(() => setPlans(FALLBACK));
  }, []);

  return (
    <>
      <div
        data-testid="upclub-banner"
        className="relative overflow-hidden rounded-2xl p-6 md:p-8 my-6 shadow-xl border border-indigo-500/30 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white"
      >
        <div
          aria-hidden
          className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-30 blur-3xl"
          style={{ background: "radial-gradient(circle, #6366f1 0%, transparent 70%)" }}
        />
        <div
          aria-hidden
          className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full opacity-20 blur-3xl"
          style={{ background: "radial-gradient(circle, #2563EB 0%, transparent 70%)" }}
        />

        <div className="relative max-w-3xl mx-auto text-center">
          <span className="inline-flex items-center gap-1.5 bg-indigo-500/20 text-indigo-200 text-[11px] font-semibold px-3 py-1 rounded-full uppercase tracking-[0.2em] border border-indigo-400/30">
            <Sparkles size={12} /> Benefícios Exclusivos
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-4 mb-2">
            Entre no <span className="text-indigo-300">UP Club</span> <Zap className="inline mb-1" size={26} />
          </h2>
          <p className="text-slate-300 text-sm md:text-base mb-6 leading-relaxed">
            Película Hidrogel HD grátis a cada 3 meses, <strong>3 meses extras</strong> de garantia em reparos,{" "}
            <strong>10% off</strong> em serviços, <strong>15% off</strong> em acessórios e <strong>5% off</strong> em celulares novos.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
            <button
              onClick={() => setCheckout({ plan_type: "monthly" })}
              data-testid="upclub-monthly-btn"
              className="group bg-white/10 hover:bg-white/20 text-white border border-white/20 font-medium py-3 px-6 rounded-xl transition-all flex flex-col items-center backdrop-blur-sm"
            >
              <span className="text-[11px] uppercase tracking-wider text-slate-300 group-hover:text-white">Plano Mensal</span>
              <span className="text-xl font-bold">R$ {plans.monthly.price.toFixed(2).replace(".", ",")}<span className="text-xs font-normal opacity-70"> /mês</span></span>
            </button>

            <button
              onClick={() => setCheckout({ plan_type: "annual" })}
              data-testid="upclub-annual-btn"
              className="relative bg-indigo-500 hover:bg-indigo-400 text-white font-semibold py-3 px-6 rounded-xl transition-all shadow-lg shadow-indigo-500/40 flex flex-col items-center border border-indigo-300/30"
            >
              <span className="absolute -top-2 right-3 bg-amber-400 text-slate-900 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                Melhor preço
              </span>
              <span className="text-[11px] uppercase tracking-wider text-indigo-100">Plano Anual</span>
              <span className="text-xl font-bold">R$ {plans.annual.price.toFixed(2).replace(".", ",")}<span className="text-xs font-normal opacity-80"> /ano</span></span>
            </button>
          </div>

          <div className="mt-4 text-xs text-slate-400">
            Pagamento seguro via Mercado Pago. Ativação automática após aprovação.
          </div>
        </div>
      </div>

      {checkout && (
        <SubscribeModal
          planType={checkout.plan_type}
          price={plans[checkout.plan_type]?.price}
          onClose={() => setCheckout(null)}
        />
      )}
    </>
  );
}

function SubscribeModal({ planType, price, onClose }) {
  const [form, setForm] = useState({ client_name: "", phone: "", email: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/public/upclub/subscribe-recurring", {
        ...form,
        plan_type: planType,
      });
      if (!data?.checkout_url) throw new Error("Sem URL de checkout");
      toast.success("Redirecionando pro Mercado Pago...");
      try { localStorage.setItem("upclub_pending_id", data.member_id); } catch (err) { console.debug(err); }
      window.location.href = data.checkout_url;
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || "Erro";
      toast.error(typeof detail === "string" ? detail : "Erro ao iniciar checkout");
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <form
        onSubmit={submit}
        data-testid="upclub-subscribe-modal"
        className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl"
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-indigo-600 font-semibold">
              UP Club — {planType === "annual" ? "Anual" : "Mensal"}
            </div>
            <h2 className="font-display text-2xl font-bold text-slate-900 mt-1">
              R$ {(price || 0).toFixed(2).replace(".", ",")}
              <span className="text-sm text-slate-500 font-normal">
                {planType === "annual" ? " /ano" : " /mês"}
              </span>
            </h2>
          </div>
          <button type="button" onClick={onClose} data-testid="upclub-modal-close" disabled={loading}>
            <X size={20} className="text-slate-500 hover:text-slate-800" />
          </button>
        </div>

        <div className="mb-4 flex items-start gap-2 bg-indigo-50 border border-indigo-200 rounded-sm p-3">
          <RefreshCw size={14} className="text-indigo-600 flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-indigo-800 leading-relaxed">
            <strong>Renovação automática:</strong> autoriza seu cartão UMA vez → o Mercado Pago cobra automaticamente todo período → você mantém os benefícios sempre ativos. <strong>Cancela quando quiser</strong> em <a href="/upclub/manage" className="underline">upclub/manage</a>.
          </p>
        </div>

        <div className="space-y-3">
          <FieldMini label="Seu nome *">
            <input
              type="text" required minLength={2}
              value={form.client_name}
              onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              data-testid="upclub-modal-name"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm text-slate-900 placeholder:text-slate-400 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Como aparece no seu CPF"
            />
          </FieldMini>
          <FieldMini label="WhatsApp *">
            <input
              type="tel" required minLength={8}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="upclub-modal-phone"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm text-slate-900 placeholder:text-slate-400 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="49 99999-9999"
            />
          </FieldMini>
          <FieldMini label="Email *">
            <input
              type="email" required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="upclub-modal-email"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm text-slate-900 placeholder:text-slate-400 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="cliente@email.com"
            />
          </FieldMini>
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="upclub-modal-submit"
          className="w-full mt-5 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white font-semibold py-3 rounded-sm text-sm shadow-lg shadow-indigo-500/30"
        >
          {loading ? (
            <><Loader className="animate-spin" size={14} /> Abrindo Mercado Pago...</>
          ) : (
            <><ShieldCheck size={14} /> Autorizar Assinatura Automática</>
          )}
        </button>
        <p className="text-[10px] text-slate-400 text-center mt-2">
          🔒 Pagamento processado pelo Mercado Pago. <a href="/upclub/manage" className="text-indigo-500 hover:underline">Cancelar assinatura</a>
        </p>
      </form>
    </div>
  );
}

function FieldMini({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-slate-600 font-semibold mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
